<template>
  <div :class="wrapperClass" v-on-click-outside="closeMenu">
    <div :class="triggerClass" @click="toggle">
      <span :class="placeholderClass">{{ text }}</span>
      <span :class="{
        [arrowClass]: true,
        'border-l': divider
      }">
        <arrow-down class="h-4 w-4 fill-current"></arrow-down>
      </span>
    </div>
    <transition name="fade" mode="out-in">
      <div v-if="open" :class="{
        [dropdownWrapperClass]: true,
        'max-h-full': compress
      }" :style="{ maxHeight }">
        <div v-for="(action, index) in actions" :key="index" :class="{
          [actionClass]: true
        }" @click="callAction(action.callback)">
          {{ action.text }}
        </div>
        <template v-if="styled">
          <div v-for="(option, index) in formattedOptions" :key="option.text" :class="dropdownClass(option, index)"
            :style="getStyle(styled, option)" @click="select(option)">
            {{ option.text }}
          </div>
        </template>
        <template v-else>
          <div v-for="(option, index) in formattedOptions" :key="option.value" :class="dropdownClass(option, index)"
            @click="select(option)">
            {{ option.text }}
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script>
import { twJoin } from 'tailwind-merge';
import ArrowDown from '../assets/icons/chevron-down.svg';
import directives from '../directives';

export default {
  name: 'GdDropdown',
  components: {
    ArrowDown
  },
  directives,
  props: {
    compress: {},
    value: {},
    placeholder: {},
    options: {},
    variant: { type: String, default: 'base' },
    size: { type: String, default: 'regular' },
    actions: {
      default: () => []
    },
    styled: null,
    maxHeight: { default: '400px' },
    disabled: Boolean,
    divider: { type: Boolean, default: false }
  },
  data() {
    return { open: false };
  },
  computed: {
    wrapperClass() {
      const baseClass = 'relative h-10 select-none';
      const sizeClass = {
        regular: 'w-40',
        small: 'w-24',
        'x-small': 'w-16',
        lg: 'w-56',
        full: 'w-full',
      };
      return twJoin(baseClass, sizeClass[this.size]);
    },
    triggerClass() {
      const baseClass = 'border rounded-lg h-full flex items-center';
      const variantClass = {
        base: 'bg-white border-grey-200',
        dark: 'bg-secondary border-secondary-light text-secondary-invert'
      };
      return twJoin(
        baseClass,
        variantClass[this.variant],
        this.disabled ? 'cursor-not-allowed filter contrast-75' : 'cursor-pointer',
        this.open ? 'rounded-b-none' : ''
      );
    },
    dropdownWrapperClass() {
      const baseClass =
        'border border-t-0 rounded-lg rounded-t-none shadow-lg relative z-10 w-full overflow-auto custom-scrollbars';
      const variantClass = {
        base: 'bg-white border-grey-200',
        dark: 'bg-secondary border-secondary-light'
      };
      return twJoin(baseClass, variantClass[this.variant]);
    },
    actionClass() {
      const baseClass = 'px-4 py-2 select-none cursor-pointer text-sm md:text-base';
      const variantClass = {
        base: 'hover:bg-grey-300 text-dark ',
        dark:
          'hover:bg-secondary-darker text-secondary-invert border-secondary-light'
      };
      return twJoin(baseClass, variantClass);
    },
    placeholderClass() {
      const baseClass = 'flex-grow select-none text-left px-4 py-2 truncate text-sm md:text-base';
      const value = this.formattedOptions.find(
        option => option.value === this.value
      );
      const placeholderClass = !this.value && !value ? 'opacity-50' : '';
      const variantClass = {
        base: 'text-grey-500',
        dark: 'text-secondary-invert'
      };
      return twJoin(baseClass, placeholderClass, variantClass[this.variant]);
    },
    arrowClass() {
      const baseClass = 'self-stretch inline-flex items-center p-2';
      const variantClass = {
        base: 'text-grey-500 border-grey-200',
        dark: 'text-secondary-invert border-secondary-lighter'
      };
      return twJoin(baseClass, variantClass[this.variant], this.disabled ? 'cursor-not-allowed' : 'cursor-pointer');
    },
    text() {
      const placeholder = this.placeholder || '-';
      let value;
      if (this.styled) {
        value = this.formattedOptions.find(
          option => option.value.name === this.value
        );
      } else {
        value = this.formattedOptions.find(
          option => option.value === this.value
        );
      }

      // If the value has a showValue property, use that
      if (value?.showValue !== undefined && value?.showValue?.length > 0) {
        return value.showValue;
      }

      return value ? value.text : placeholder;
    },
    formattedOptions() {
      if (!this.options) return [];
      if (this.options[0] && typeof this.options[0].value !== 'undefined') return this.options;
      return this.options.map(option => ({ text: option, value: option }));
    }
  },
  methods: {
    getStyle(styled, option) {
      return { [styled]: option.value.name };
    },
    callAction(action) {
      this.open = false;
      action();
    },
    toggle() {
      this.open = this.disabled ? false : !this.open;
    },
    select(option) {
      if (option.value === '---') {
        return;
      }
      this.open = false;
      this.$emit('input', option.value);
      this.$emit('change', option.value);
      this.$emit('changeValue', option.value);
    },
    closeMenu() {
      this.open = false;
    },
    dropdownClass(option, index) {
      const separatorVariant = {
        base: 'border-grey-300',
        dark: 'border-secondary-lightest'
      }
      const baseClass = 'select-none cursor-pointer text-left';
      const separatorClass = option.value === '---' ? `${separatorVariant[this.variant]} pointer-events-none` : 'px-4 py-2';
      const selectedClass = option.value === this.value ? 'font-bold' : '';
      const lastItemClass = index === this.formattedOptions.length - 1 ? 'rounded-lg rounded-t-none' : 'border-b';
      const variantClass = {
        base: 'hover:bg-grey-100 text-dark border-grey-200',
        dark:
          'hover:bg-secondary-darker text-secondary-invert border-secondary-light'
      }
      return twJoin(
        baseClass,
        separatorClass,
        selectedClass,
        lastItemClass,
        variantClass[this.variant]
      );
    }
  }
};
</script>

